<template>
  <OverlayLoader :show="loadModal" />
  <div class="container-fluid px-4">
    <!-- <div class="d-flex justify-content-between align-items-center mb-3"> -->
      <div class="d-flex flex-wrap justify-content-between align-items-center gap-2 w-100">
        <GoBackButton />
        <h4 class="text-success text-center mb-0">Users Summary</h4>
        <MaterialButton class="mx-2" @click="openAddUserModal()">Add User</MaterialButton>
        <button class="btn-refresh mx-2" @click="refreshTable()"><i class="material-icons text-sm align-middle">refresh</i> Refresh</button>
      </div>
    <!-- </div> -->
    <BaseTable heading="Users Summary" :rows="users" :columns="columns" sortable searchable :selectable="false"
      :loading="loading" :pagination="pagination" @changePage="onChangePage" @updatePageSize="onUpdatePageSize"
      @searchChanged="onSearchChanged">
      <!-- Assignments -->
      <template #col-assignments="{ row }">
        <button @click="openAssignmentsModal(row)" class="btn btn-sm btn-outline-primary my-0">
          View
        </button>
      </template>

      <!-- Skills Assignment Count -->
      <template #col-skillsAssignmentCount="{ row }">
        <ul class="mb-0 ps-3 list-unstyled" v-if="row.skillsAssignmentCount?.length">
          <li v-for="(skill, i) in row.skillsAssignmentCount" :key="i">{{ skill }}</li>
        </ul>
        <span v-else class="text-muted">None</span>
      </template>

      <!-- Preferred Activities & Workgroups -->
      <template #col-preferredActivitiesWorkgroups="{ row }">
        <div>
          <strong>Activities:</strong>
          <ul v-if="row.preferredActivitiesWorkgroups?.filter(i => i.heading === 'activities').length"
            class="ps-3 list-unstyled">
            <li v-for="(act, i) in row.preferredActivitiesWorkgroups.filter(i => i.heading === 'activities')"
              :key="'act-' + i">
              {{ act.Text }}
            </li>
          </ul>
          <span v-else class="text-muted">None</span>
        </div>

        <div class="mt-2">
          <strong>Workgroups:</strong>
          <ul v-if="row.preferredActivitiesWorkgroups?.filter(i => i.heading === 'workgroups').length"
            class="ps-3 list-unstyled">
            <li v-for="(wg, i) in row.preferredActivitiesWorkgroups.filter(i => i.heading === 'workgroups')"
              :key="'wg-' + i">
              {{ wg.Text }}
            </li>
          </ul>
          <span v-else class="text-muted">None</span>
        </div>
      </template>

      <!-- Interest Skills -->
      <template #col-interestskills="{ row }">
        <span v-if="row.interestskills?.length">{{ row.interestskills.join(', ') }}</span>
        <span v-else class="text-muted">No interests</span>
      </template>

      <!-- Preferences -->
      <template #col-preferences="{ row }">
        <button v-if="row.preferences != 'none'" class="btn btn-sm btn-outline-info my-0" @click="openSchedule(row)">
          <i class="material-icons text-sm align-middle">event</i>
          {{ row.preferences }}
        </button>

        <button v-else class="btn btn-sm p-0 my-0">
          {{ row.preferences }}
        </button>
      </template>

      <!-- Actions -->
      <template #actions="{ row }">
        <button class="btn btn-sm my-0 py-0" @click="editUser(row.id)">
          <i class="material-icons text-lg text-orange">edit</i>
        </button>
      </template>
    </BaseTable>
    <ContentModal v-model="showModal" title="User Preferences">
      <template #default>
        <div v-if="personPreferences">
          <h6 class="d-flex justify-content-end">
            <a :href="personPreferences.links?.upcoming_assignments" target="_blank"
              class="text-info text-underline">View Assignments</a>
          </h6>

          <div class="d-flex gap-8">
            <span class="d-flex gap-2">
              <h6 class="mb-0">Name:</h6>
              <p class="mb-0">{{ personPreferences.user_info?.name }}</p>
            </span>
            <span class="d-flex gap-2">
              <h6 class="mb-0">Email:</h6>
              <p class="mb-0">{{ personPreferences.user_info?.email }}</p>
            </span>
          </div>

          <h6 class="mt-4">Summary:</h6>
          <ul v-if="personPreferences.summary?.length" class="mb-0">
            <li v-for="(sentence, i) in personPreferences.summary" :key="'summary-' + i">{{ sentence }}</li>
          </ul>
          <div v-else class="text-muted">No preference summary available.</div>

          <h6 class="mt-4">Preferred Activities:</h6>
          <div v-if="personPreferences.activity_preferences?.interested_in_any_activity" class="text-muted">
            Interested in any activity.
          </div>
          <div v-else-if="!personPreferences.activity_preferences?.preferred_activities?.length" class="text-muted">
            No preferred activities.
          </div>
          <ul v-else class="mb-0">
            <li v-for="activity in personPreferences.activity_preferences.preferred_activities" :key="activity.id">
              {{ activity.name }}
            </li>
          </ul>

          <h6 class="mt-4">Preferred Workgroups:</h6>
          <div v-if="personPreferences.workgroup_preferences?.interested_in_any_workgroup" class="text-muted">
            Interested in any eligible workgroup.
          </div>
          <div v-else-if="!personPreferences.workgroup_preferences?.preferred_workgroups?.length" class="text-muted">
            No preferred workgroups.
          </div>
          <ul v-else class="mb-0">
            <li v-for="workgroup in personPreferences.workgroup_preferences.preferred_workgroups" :key="workgroup.id">
              {{ workgroup.name }}
            </li>
          </ul>

          <h6 class="mt-4">Skills:</h6>
          <MaterialSelectedBadge v-for="skill in personPreferences.skills" :key="skill.id"
            :label="`${skill.name} (Level ${skill.level})`" color="info" />

          <h6 class="mt-4">Interests:</h6>
          <div class="d-flex flex-wrap">
            <MaterialSelectedBadge v-for="interest in personPreferences.interest_skills" :key="interest.id"
              :label="interest.name" color="info" />
          </div>

          <h6 class="mt-4">Availability:</h6>
          <ul v-if="personPreferences.availability?.length" class="mb-0">
            <li v-for="(item, i) in personPreferences.availability" :key="'availability-' + i">{{ item }}</li>
          </ul>
          <div v-else class="text-muted">No availability records.</div>

          <h6 class="mt-4">Rules:</h6>
          <ul v-if="personPreferences.scheduling_rules?.length" class="mb-0">
            <li v-for="(rule, i) in personPreferences.scheduling_rules" :key="'rule-' + i">
              {{ rule.description || rule.rule_description || rule.label || 'Scheduling rule configured.' }}
            </li>
          </ul>
          <div v-else class="text-muted">No scheduling rules.</div>
        </div>
        <div v-else>
          <p>No preferences found.</p>
        </div>
      </template>
    </ContentModal>

    <!-- Modal for Add User -->
    <Modal v-if="isAddUserModalOpen" :modalHeading="addUserModalHeading" :modalContent="addUserModalContent"
      :iframeUrl="addUserIframeUrl" size="2xl" refresh-on-close @close="closeAddUserModal" />

    <!-- Modal for Assignments -->
    <Modal v-if="isAssignmentsModalOpen" :modalHeading="assignmentsModalHeading" :modalContent="assignmentsModalContent"
      :iframeUrl="assignmentsIframeUrl" size="2xl" @close="closeAssignmentsModal" />
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from "vue";
import { getLocationId } from "@/utils/SessionDataHandler";
import BaseTable from "@/components/Table/Table.vue";
import ContentModal from "@/components/Modal/ContentModal.vue";
import Modal from "@/components/Modal/Modal.vue";
import OverlayLoader from "@/components/Loader/OverlayLoader.vue";
import MaterialButton from "../../components/common/MaterialButton.vue";
import GoBackButton from "../../components/common/GoBackButton.vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";

const router = useRouter();
import { baseUrl } from "@/utils/BaseUrl.js";
    
const store = useStore();
const locationId = getLocationId();
const searchTerm = ref("");
const loadModal = ref(false);
const showModal = ref(false);
const personPreferences = ref(null);

// Add User Modal state
const isAddUserModalOpen = ref(false);
const addUserModalHeading = ref("Add User");
const addUserModalContent = ref("");
const addUserIframeUrl = ref("");

// Assignments Modal state
const isAssignmentsModalOpen = ref(false);
const assignmentsModalHeading = ref("User Assignments");
const assignmentsModalContent = ref("");
const assignmentsIframeUrl = ref("");


// 🧠 Table Columns
const columns = [
  { key: "id", label: "ID" },
  { key: "firstName", label: "First Name" },
  { key: "lastName", label: "Last Name" },
  { key: "email", label: "Email" },
  { key: "assignments", label: "Assignments" },
  { key: "assignmentcount", label: "Assignment Count" },
  { key: "skillsAssignmentCount", label: "Skills Assignment Count" },
  { key: "preferredActivitiesWorkgroups", label: "Preferred Activities & Workgroups" },
  { key: "preferences", label: "Preferences" },
  { key: "interestskills", label: "Interest Skills" },
];

// 🔁 Vuex State
const users = computed(() => store.getters.allUsersSummary);
const loading = computed(() => store.getters.usersSummaryLoading);
const pagination = computed(() => store.getters.usersSummaryPagination);

// 🔧 Fetch Data
const fetchUsers = async (page = 1, pageSize = pagination.value.page_size || 10) => {
  await store.dispatch("fetchUsersSummary", {
    locationId,
    search: searchTerm.value,
    page,
    pageSize,
  });
};

const openSchedule = async (row) => {
  loadModal.value = true;
  if (!row || !row.id) {
    loadModal.value = false;
    return;
  }
  const res = await store.dispatch("fetchPersonPreferences", {
    personId: row.id,
    locationId,
  });
  personPreferences.value = res?.preferences || null;
  showModal.value = true;
  loadModal.value = false;
};

// 🚀 Initial Load
onMounted(() => fetchUsers());

// 📄 Pagination Handlers
const onChangePage = async (page) => await fetchUsers(page);
const onUpdatePageSize = async (size) => await fetchUsers(1, size);

// 🔍 Search Handler
const onSearchChanged = async (term) => {
  searchTerm.value = term;
  await fetchUsers(1);
};

const openAddUserModal = () => {
  addUserIframeUrl.value = `${baseUrl}/MF/cs-mf-addusers/`;
  addUserModalHeading.value = "Add User";
  isAddUserModalOpen.value = true;
};

const closeAddUserModal = () => {
  isAddUserModalOpen.value = false;
  addUserIframeUrl.value = "";
  refreshTable();
};

const refreshTable = () => {
  fetchUsers(pagination.value.current_page || 1);
};


const openAssignmentsModal = (row) => {
  if (row && row.assignments) {
    assignmentsIframeUrl.value = row.assignments;
    assignmentsModalHeading.value = `Assignments - ${row.firstName} ${row.lastName}`;
    isAssignmentsModalOpen.value = true;
  }
};

const closeAssignmentsModal = () => {
  isAssignmentsModalOpen.value = false;
  assignmentsIframeUrl.value = "";
};

const editUser = (id) => {
  router.push({ name: "EditUser", params: { userid: id } });
};

</script>

<style scoped>
.btn-refresh {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
  border: none;
  border-radius: 20px;
  padding: 6px 18px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.4);
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.btn-refresh:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 14px rgba(102, 126, 234, 0.55);
}
.btn-refresh:active {
  transform: translateY(0);
  box-shadow: 0 2px 6px rgba(102, 126, 234, 0.3);
}
</style>
